import { Tag } from "antd";
import React from "react";
import { HTMLProps } from "../model/HTMLProps";

interface InterviewMetadataBadgeProps extends HTMLProps {
  icon?: React.ReactNode;
  color?: string;
  size?: "small" | "large";
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

function InterviewMetadataTag(props: InterviewMetadataBadgeProps) {
  const isSmall = props.size === "small";
  const fontSize = isSmall ? "0.75rem" : "0.82rem";
  const padding = isSmall ? "0 8px" : "2px 10px";
  const lineHeight = isSmall ? "20px" : "22px";

  return (
    <Tag
      color={props.color || "default"}
      className={`border-0 d-inline-flex align-items-center ${props.className || ""}`}
      style={{
        fontSize: fontSize,
        padding: padding,
        lineHeight: lineHeight,
        borderRadius: "4px",
        marginRight: isSmall ? "4px" : "0",
        ...props.style
      }}
    >
      {props.icon && (
        <span style={{ marginRight: 6, display: "inline-flex", alignItems: "center" }}>
          {props.icon}
        </span>
      )}
      {props.children}
    </Tag>
  );
}

export function InterviewRoundsBadge(props: { count?: number; size?: "small" | "large" }) {
  if (props.count == null || props.count <= 0) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="fas fa-list-ol" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.count} {props.count === 1 ? "Round" : "Rounds"}
    </InterviewMetadataTag>
  );
}

export function InterviewQuestionsBadge(props: { count?: number; size?: "small" | "large" }) {
  if (props.count == null || props.count <= 0) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="far fa-lightbulb" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.count} {props.count === 1 ? "Question" : "Questions"}
    </InterviewMetadataTag>
  );
}

export function InterviewDomainBadge(props: {
  domain?: string;
  showSuffix?: boolean;
  size?: "small" | "large"
}) {
  const displayDomain = props.domain?.toLowerCase() === "other" ? undefined : props.domain;

  if (!displayDomain) return null;
  return (
    <InterviewMetadataTag
      color="orange"
      size={props.size}
      icon={<i className="fas fa-briefcase" style={{ fontSize: "0.9em" }}></i>}
    >
      {displayDomain}
    </InterviewMetadataTag>
  );
}

export function InterviewLevelBadge(props: { level?: string; size?: "small" | "large" }) {
  if (!props.level) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="fas fa-chart-line" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.level}
    </InterviewMetadataTag>
  );
}

export function InterviewYOEBadge(props: { yoe?: number; size?: "small" | "large" }) {
  if (props.yoe == null || props.yoe <= 0) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="fas fa-history" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.yoe} YOE
    </InterviewMetadataTag>
  );
}

export function InterviewLocationBadge(props: { city?: string; country?: string; size?: "small" | "large" }) {
  const display = [props.city, props.country].filter(Boolean).join(", ");
  if (!display) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="fas fa-map-marker-alt" style={{ fontSize: "0.9em" }}></i>}
    >
      {display}
    </InterviewMetadataTag>
  );
}

export function InterviewDateBadge(props: { date?: Date | string; size?: "small" | "large"; children?: React.ReactNode }) {
  if (!props.date && !props.children) return null;
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="far fa-calendar-alt" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.children}
    </InterviewMetadataTag>
  );
}

export function InterviewUserBadge(props: { size?: "small" | "large"; children: React.ReactNode }) {
  return (
    <InterviewMetadataTag
      color="default"
      size={props.size}
      icon={<i className="far fa-user" style={{ fontSize: "0.9em" }}></i>}
    >
      {props.children}
    </InterviewMetadataTag>
  );
}
